import { Toast } from './toast';
import {
  axiosInstance,
  cancelTokenSource,
  statusHelper,
  getServiceUrl,
  getAuthToken,
  scrollHelper
} from './utilities';
import { ndcApi } from './apiVariables';

const SAMFIN_KYC_TOKEN_KEY = 'samfinKycToken';
const SAMFIN_KYC_TOKEN_TTL_MS = 60 * 60 * 1000; // 1 hour

let samfinKycTokenRequest = null;

const readCachedSamfinKycToken = () => {
  try {
    const { token, expiresAt } =
      JSON.parse(localStorage.getItem(SAMFIN_KYC_TOKEN_KEY)) || {};
    return token && Date.now() < expiresAt ? token : null;
  } catch (e) {
    return null;
  }
};

export const clearSamfinKycToken = () =>
  localStorage.removeItem(SAMFIN_KYC_TOKEN_KEY);

// Token for 'testAgfinKyc' APIs: reuse the cached one for an hour, else
// fetch a new one from platform-setup (concurrent callers share one request)
const getSamfinKycToken = async () => {
  const cached = readCachedSamfinKycToken();
  if (cached) return cached;

  if (!samfinKycTokenRequest) {
    const { getTokenApi } = ndcApi;
    getTokenApi.query = { serviceName: 'SamfinKyc' };
    // eslint-disable-next-line no-use-before-define
    samfinKycTokenRequest = api({ ...getTokenApi, cancelToast: false })
      .then((res) => {
        const accessToken = res?.response?.accessToken || res?.accessToken;
        if (!accessToken) {
          throw new Error(res?.message || 'Unable to get SamfinKyc token');
        }
        localStorage.setItem(
          SAMFIN_KYC_TOKEN_KEY,
          JSON.stringify({
            token: accessToken,
            expiresAt: Date.now() + SAMFIN_KYC_TOKEN_TTL_MS
          })
        );
        return accessToken;
      })
      .finally(() => {
        samfinKycTokenRequest = null;
      });
  }
  return samfinKycTokenRequest;
};

export const api = async function ({
  method = 'get',
  api: apiURL,
  body,
  status = false,
  baseURL = 'user',
  token = null,
  cancelToast = true,
  cancelLogout = false,
  cancelErrorToast = false,
  configObj = {}
}) {
  const tempConfigObj = { ...configObj };

  if (cancelTokenSource.token)
    tempConfigObj.cancelToken = cancelTokenSource.token;

  const isSamfinKyc = baseURL === 'testAgfinKyc';
  const samfinKycToken = isSamfinKyc ? await getSamfinKycToken() : null;

  const getValidToken = () => {
    const apiKey = process.env.REACT_APP_API_GATEWAY_SUBSCRIPTION_KEY;
    axiosInstance.defaults.headers.common['x-api-key'] = apiKey;

    if (isSamfinKyc) {
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${samfinKycToken}`;
    } else if (token || getAuthToken()) {
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${
        token || getAuthToken()
      }`;
    }
  };

  return await new Promise((resolve, reject) => {
    getValidToken();

    axiosInstance[method](
      `${getServiceUrl(baseURL)}${apiURL}`,
      body || '',
      tempConfigObj
    )
      .then((data) => {
        resolve(statusHelper(status, data));
        if (cancelToast && data?.data?.message) {
          Toast({
            type: data?.data?.success ? 'success' : 'error',
            message: data?.data?.message
          });
        }
        if (
          data?.data?.success &&
          (method === 'put' || method === 'put' || method === 'delete')
        ) {
          scrollHelper({ isTop: true, isSmooth: true });
        }
      })
      .catch((error) => {
        try {
          if (error.response) {
            // Rejected SamfinKyc token: fetch a fresh one on the next call
            if (isSamfinKyc && error.response.status === 401) {
              clearSamfinKycToken();
            }
            reject(statusHelper(status, error.response, cancelLogout));
            // Callers with cancelErrorToast show the backend message themselves
            if (!cancelErrorToast) {
              Toast({
                type: 'error',
                message: error?.data?.message || error?.message
              });
            }
          } else {
            reject(error);
          }
        } catch (err) {
          reject(err);
        }
      });
  });
};
